import React, { useCallback, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { ChevronDown, ChevronUp, UserPlus, X } from 'lucide-react';
import { components } from 'react-select';
import { toast } from 'sonner';
import { ABORT_ERROR, makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import AdvancedSelect from '../common/AdvancedSelect';
import { useDebouncedAbortableLoader } from '../HoaDirectory/useHoaDirectorySearch';
import ContactCard from './ContactCard';
import ContactEditModal from './ContactEditModal';
import { getEmailValidationError, isPreferredContactForAssociation } from '../HoaDirectory/HoaDirectoryHelper';
import FormattedPhoneField from './FormattedPhoneField';
import AlertModal from '../AlertModal';

const ContactOption = props => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="fw-bold midFont mb-0">{data.label}</div>
      {data.subtitle ? <div className="tinyFont text-muted">{data.subtitle}</div> : null}
    </components.Option>
  );
};

const blankDraft = () => ({ name: '', title: '', directPhone: '', directEmail: '' });

const toContactOption = (c, index) => ({
  label: c.name || 'N/A',
  value: c._id
    ? String(c._id)
    : [c.name, c.directEmail, c.directPhone].filter(Boolean).join('|') || `draft-${index}`,
  subtitle: [c.title, c.directEmail].filter(Boolean).join(' · '),
  raw: c,
});

/**
 * Link / unlink contacts on the HOA form snapshot.
 * New contacts have no _id until order save; the backend creates them then.
 */
const ContactPicker = ({
  contacts = [],
  onLinkContact,
  onUnlinkContact,
  onUpdateContact,
  disabled,
  contactType,
  directoryId,
  isOrderContext = false,
  /** New directory entry — no existing contacts to search; show add form only. */
  addContactOnly = false,
  /** Edit Association only — show star toggle for preferred contact. */
  showPreferredContactToggle = false,
  /** Association context for starring a preferred contact (saved on directory submit). */
  preferredAssociationId,
  /** Batch star/unstar in Edit Association modal. */
  onPreferredContactChange,
}) => {
  const linkedContacts = useMemo(
    () => (contacts || []).filter(c => c && (c._id || c.name || c.directEmail || c.directPhone)),
    [contacts]
  );

  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(blankDraft);
  const [editingContact, setEditingContact] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [contactPendingRemoval, setContactPendingRemoval] = useState(null);
  const [draftEmailError, setDraftEmailError] = useState('');

  const preferredContextId =
    preferredAssociationId || (contactType === 'association' ? directoryId : null);

  const showPreferredBadge = Boolean(
    preferredContextId &&
      (contactType === 'association' || contactType === 'managementCompany')
  );

  const showPreferredToggle =
    showPreferredContactToggle &&
    !disabled &&
    Boolean(onPreferredContactChange) &&
    (contactType === 'association' ||
      (contactType === 'managementCompany' && Boolean(preferredAssociationId)));

  const contactOptions = useMemo(
    () => linkedContacts.map((contact, index) => toContactOption(contact, index)),
    [linkedContacts]
  );

  const searchContacts = useCallback(
    async (q, signal) => {
      const requestBody = {
        query: q,
        pageSize: 20,
        pageNumber: 1,
        ...(contactType ? { contactType } : {}),
      };

      // Filter by directory ID if provided
      if (directoryId) {
        if (contactType === 'association') {
          requestBody.associationId = directoryId;
        } else if (contactType === 'managementCompany') {
          requestBody.managementCompanyId = directoryId;
        }
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.CONTACTS_SEARCH,
        method: 'POST',
        requestBody,
        signal,
      });

      if (error === ABORT_ERROR) return [];

      if (!error && Array.isArray(response?.results)) {
        // Filter out contacts that are already linked
        const linkedIds = new Set(linkedContacts.map(c => String(c._id)).filter(Boolean));
        return response.results
          .filter(c => !linkedIds.has(String(c._id)))
          .map((contact, index) => toContactOption(contact, index));
      }

      return [];
    },
    [contactType, directoryId, linkedContacts]
  );

  const loadOptions = useDebouncedAbortableLoader(searchContacts);

  const closePicker = () => {
    setOpen(false);
    setAdding(false);
    setDraft(blankDraft());
    setDraftEmailError('');
  };

  const linkContact = contact => {
    if (!contact) return;
    if (!contact._id && !contact.name && !contact.directEmail && !contact.directPhone) {
      toast.error('Contact must have at least a name, email, or phone number');
      return;
    }
    onLinkContact(contact);
    closePicker();
  };

  const handleChange = option => {
    if (!option?.raw) return;
    linkContact(option.raw);
  };

  const handleCreateOption = input => {
    setDraft({
      name: String(input || '').trim(),
      title: '',
      directPhone: '',
      directEmail: '',
    });
    setAdding(true);
  };

  const handleSaveNew = () => {
    const name = draft.name.trim();
    if (!contactType) {
      toast.error('Contact type is required');
      return;
    }

    const emailError = getEmailValidationError(draft.directEmail);
    if (emailError) {
      setDraftEmailError(emailError);
      return;
    }
    setDraftEmailError('');

    const newContact = {
      name,
      title: draft.title.trim(),
      directPhone: draft.directPhone.trim(),
      directEmail: draft.directEmail.trim(),
      contactType,
    };

    // Add directory ID for new contacts
    if (directoryId) {
      if (contactType === 'association') {
        newContact.associationId = directoryId;
      } else if (contactType === 'managementCompany') {
        newContact.managementCompanyId = directoryId;
      }
    }

    linkContact(newContact);
  };

  const handleEdit = contact => {
    setEditingContact(contact);
    setShowEditModal(true);
  };

  const handleEditSave = updatedContact => {
    // Update the contact directly in the contacts array
    // This ensures the edited contact replaces the old one
    onUpdateContact?.(editingContact, updatedContact);
  };

  const handleUnlink = contact => {
    if (!contact._id) {
      // New contact not yet saved, just unlink
      onUnlinkContact(contact);
      return;
    }
    setContactPendingRemoval({ contact, mode: 'unlink' });
  };

  const handleDelete = contact => {
    if (!contact._id) {
      onUnlinkContact(contact);
      return;
    }
    setContactPendingRemoval({ contact, mode: 'delete' });
  };

  const handleConfirmRemoval = () => {
    if (!contactPendingRemoval) return;
    onUnlinkContact(contactPendingRemoval.contact);
    setContactPendingRemoval(null);
  };

  const handleTogglePreferred = (contact, preferred) => {
    if (!contact || !onPreferredContactChange) return;
    onPreferredContactChange(contact, preferred);
  };

  return (
    <div>
      {linkedContacts.length > 0 && (
        <div
          className="mb-2"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${linkedContacts.length < 3 ? 2 : 3}, 1fr)`,
            gap: 8,
          }}
        >
          {linkedContacts.map((contact, index) => (
              <ContactCard
                key={contact._id ? String(contact._id) : `new-${index}`}
                contact={contact}
                index={index}
                onEdit={!disabled ? handleEdit : null}
                onUnlink={!disabled && onUnlinkContact && isOrderContext ? handleUnlink : null}
                onDelete={!disabled && onUnlinkContact && !isOrderContext ? handleDelete : null}
                showPreferredToggle={showPreferredToggle}
                isPreferred={
                  showPreferredBadge
                    ? isPreferredContactForAssociation(contact, preferredContextId)
                    : false
                }
                onTogglePreferred={showPreferredToggle ? handleTogglePreferred : null}
              />
            ))}
        </div>
      )}

      <button
        type="button"
        className="btn btn-sm btn-block d-inline-flex align-items-center text-left text-info hover-bg-info shadow-none"
        style={{
          gap: 6,
          border: '1px dashed var(--info)',
          borderRadius: 10,
        }}
        disabled={disabled}
        onClick={() => {
          if (open) closePicker();
          else {
            setOpen(true);
            if (addContactOnly) setAdding(true);
          }
        }}
      >
        <UserPlus size={14} className="flex-shrink-0" />
        <span>{open ? 'Close contact list' : addContactOnly ? 'Add a contact' : 'Link a contact'}</span>
        {open ? (
          <ChevronUp size={12} className="ml-auto flex-shrink-0" />
        ) : (
          <ChevronDown size={12} className="ml-auto flex-shrink-0" />
        )}
      </button>

      {open && (
        <>
          <div
            className="border bg-white mt-1 p-2"
            style={
              addContactOnly
                ? { borderRadius: 10 }
                : { borderTopRightRadius: 10, borderTopLeftRadius: 10 }
            }
          >
            {!addContactOnly && (
              <>
                <div className="tinyFont text-muted mb-2">
                  {linkedContacts.length} linked contact{linkedContacts.length !== 1 ? 's' : ''}
                  {' · '}
                  search directory contacts below
                </div>
                <AdvancedSelect
                  creatable
                  searchable
                  isMulti={false}
                  isClearable={false}
                  height="31px"
                  disabled={disabled || adding}
                  placeholder="Search or add a contact…"
                  items={contactOptions}
                  selectedItems={null}
                  loadOptions={loadOptions}
                  onChange={handleChange}
                  onCreateOption={handleCreateOption}
                  CustomOptionComponent={ContactOption}
                  creatableLabel="Add contact"
                  noOptionsMessage="No contacts — type a name to add…"
                  useMenuPortal
                />
              </>
            )}

            {(adding || addContactOnly) && (
              <div
                className="mt-2 p-3"
                style={{ border: '1px solid #ffb74d', borderRadius: 10, background: '#fff8f0' }}
              >
                <div className="small font-weight-bold mb-2" style={{ color: '#e65100' }}>
                  New contact
                </div>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Full name</Form.Label>
                  <input
                    className="form-control form-control-sm"
                    value={draft.name}
                    onChange={e => setDraft({ ...draft, name: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Title / Role</Form.Label>
                  <input
                    className="form-control form-control-sm"
                    value={draft.title}
                    onChange={e => setDraft({ ...draft, title: e.target.value })}
                  />
                </Form.Group>
                <div className="form-row">
                  <Form.Group className="col-6 mb-2">
                    <Form.Label className="small mb-0">Direct phone</Form.Label>
                    <FormattedPhoneField
                      value={draft.directPhone}
                      onChange={e => setDraft({ ...draft, directPhone: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="col-6 mb-2">
                    <Form.Label className="small mb-0">Direct email</Form.Label>
                    <Form.Control
                      type="email"
                      size="sm"
                      value={draft.directEmail}
                      isInvalid={Boolean(draftEmailError)}
                      onChange={e => {
                        setDraft({ ...draft, directEmail: e.target.value });
                        if (draftEmailError) setDraftEmailError('');
                      }}
                    />
                    {draftEmailError && (
                      <p className="xsmallFont text-rush mb-0 mt-1">{draftEmailError}</p>
                    )}
                  </Form.Group>
                </div>
                <div className="d-flex">
                  <Button size="sm" variant="primary" onClick={handleSaveNew}>
                    Add contact
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="ml-2"
                    onClick={() => {
                      setAdding(false);
                      setDraft(blankDraft());
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
          {!adding && !addContactOnly && (
            <button
              type="button"
              className="btn-block d-flex align-items-center justify-content-start text-info midFont mb-0 px-2 py-1 shadow-none hover-info-soft"
              style={{
                background: '#e6f1fb',
                border: '1px solid #dee2e6',
                borderTop: addContactOnly ? '1px solid #dee2e6' : '1px solid var(--info)',
                borderBottomRightRadius: 10,
                borderBottomLeftRadius: 10,
                gap: 6,
              }}
              disabled={disabled}
              onClick={() => {
                setDraft(blankDraft());
                setAdding(true);
              }}
            >
              <UserPlus size={14} className="flex-shrink-0" />
              Add new contact
            </button>
          )}
        </>
      )}

      {showEditModal && editingContact && (
        <ContactEditModal
          contact={editingContact}
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setEditingContact(null);
          }}
          onSave={handleEditSave}
        />
      )}

      <AlertModal
        show={contactPendingRemoval !== null}
        onHide={() => setContactPendingRemoval(null)}
        onDismissClick={() => setContactPendingRemoval(null)}
        onContinueClick={handleConfirmRemoval}
        alertText={
          contactPendingRemoval?.mode === 'unlink'
            ? 'Remove this contact from the order? It will stay in the directory.'
            : 'Are you sure you want to delete this contact? This will unlink it from this HOA.'
        }
        dismissButtonText="Cancel"
        continueButtonText={contactPendingRemoval?.mode === 'unlink' ? 'Unlink' : 'Delete'}
        dismissButtonVariant="secondary"
        continueButtonVariant="danger"
      />
    </div>
  );
};

export default ContactPicker;
